"use client"

import { useState } from "react"
import { API_ENDPOINTS, getApiErrorDetail, useApi } from "@/lib/api"
import { CredentialView } from "@/types/payroll"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useToast } from "@/hooks/use-toast"
import { useTranslation } from "@/hooks/use-translation"
import { formatDateTime } from "@/lib/locale"
import { Loader2 } from "lucide-react"

interface Props {
  provider: string
  credential: CredentialView | null
}

/**
 * Write-only credential editor. Reads show masks + rotation metadata (the API
 * never returns a secret); saving sends only the fields the operator changed.
 */
export function CredentialsSection({ provider, credential }: Props) {
  const { payrollApi } = useApi()
  const { toast } = useToast()
  const { t, language } = useTranslation()

  const [providerEnvironmentName, setProviderEnvironmentName] = useState(
    credential?.providerEnvironmentName ?? "sandbox"
  )
  const [clientId, setClientId] = useState("")
  const [clientSecret, setClientSecret] = useState("")
  const [isSaving, setIsSaving] = useState(false)

  const hasStored = (value?: string | null) => Boolean(value)

  const save = async () => {
    if (!clientId && !clientSecret) {
      toast({ variant: "destructive", title: t("credentials.toast.nothingEntered") })
      return
    }

    setIsSaving(true)
    try {
      await payrollApi.put(API_ENDPOINTS.PAYROLL.CREDENTIALS, {
        provider,
        providerEnvironmentName,
        clientId: clientId || null,
        clientSecret: clientSecret || null,
      })
      setClientId("")
      setClientSecret("")
      toast({ title: t("credentials.toast.saved"), description: t("credentials.toast.savedDescription") })
    } catch (err) {
      toast({
        variant: "destructive",
        title: t("credentials.toast.saveFailed"),
        description: getApiErrorDetail(err) ?? String(err),
      })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <section className="space-y-2">
      <h3 className="text-sm font-semibold">{t("credentials.title", { provider })}</h3>
      <p className="text-xs text-muted-foreground">
        {t("credentials.hint", { provider })}
        {credential?.lastRotatedOn && (
          <>
            {" "}
            {credential.lastRotatedBy
              ? t("credentials.lastChangedBy", {
                  date: formatDateTime(credential.lastRotatedOn, language),
                  name: credential.lastRotatedBy,
                })
              : t("credentials.lastChanged", { date: formatDateTime(credential.lastRotatedOn, language) })}
          </>
        )}
      </p>

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="space-y-1">
          <Label htmlFor="cred-env">{t("credentials.environmentLabel")}</Label>
          <Select value={providerEnvironmentName} onValueChange={setProviderEnvironmentName}>
            <SelectTrigger id="cred-env">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="sandbox">{t("common.env.sandbox")}</SelectItem>
              <SelectItem value="production">{t("common.env.production")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="cred-client-id">{t("credentials.clientId")}</Label>
          <Input
            id="cred-client-id"
            type="password"
            autoComplete="off"
            placeholder={hasStored(credential?.clientId) ? t("credentials.placeholderSaved") : t("credentials.placeholderUnset")}
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="cred-client-secret">{t("credentials.clientSecret")}</Label>
          <Input
            id="cred-client-secret"
            type="password"
            autoComplete="off"
            placeholder={hasStored(credential?.clientSecret) ? t("credentials.placeholderSaved") : t("credentials.placeholderUnset")}
            value={clientSecret}
            onChange={(e) => setClientSecret(e.target.value)}
          />
        </div>
      </div>

      <Button size="sm" variant="secondary" onClick={() => void save()} disabled={isSaving}>
        {isSaving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
        {t("credentials.save")}
      </Button>
    </section>
  )
}
